import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Send, Phone, Mail, User, MessageSquare, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import useEmailService from '../hooks/useEmailService';

interface ContactFormProps {
  formType?: 'contact' | 'franchise' | 'reservation' | 'newsletter';
  title?: string;
  subtitle?: string;
  onSuccess?: () => void;
  className?: string;
}

const FormContainer = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2rem;
  margin: 1rem 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`;

const FormTitle = styled.h3`
  color: #ffffff;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const FormSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin-bottom: 2rem;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: #ffffff;
  font-weight: 600;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Input = styled.input`
  padding: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none;
    border-color: #FFD700;
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.2);
    background: rgba(255, 255, 255, 0.1);
  }
  
  &:invalid {
    border-color: #8B0000;
  }
`;

const TextArea = styled.textarea`
  padding: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  min-height: 120px;
  transition: all 0.3s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none;
    border-color: #FFD700;
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.2);
    background: rgba(255, 255, 255, 0.1);
  }
`;

const SubmitButton = styled.button<{ isLoading: boolean }>`
  background: linear-gradient(135deg, #8B0000, #FFD700);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.isLoading ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  opacity: ${props => props.isLoading ? 0.7 : 1};
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #FFD700, #FFC300);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(255, 215, 0, 0.3);
  }
  
  &:disabled {
    cursor: not-allowed;
  }
`;

const FallbackButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' }>`
  padding: 1rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  margin-top: 1rem;
  
  background: ${props => props.type === 'success' 
    ? 'linear-gradient(135deg, #10B981, #34D399)' 
    : 'linear-gradient(135deg, #8B0000, #FFD700)'
  };
  color: white;
`;

const ContactForm: React.FC<ContactFormProps> = ({ 
  formType = 'contact',
  title,
  subtitle,
  onSuccess,
  className 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    subject: ''
  });

  const { 
    sendContactForm, 
    sendFranchiseInquiry, 
    sendNewsletterSignup,
    initializeEmailJS,
    fallbackToPhone,
    isLoading, 
    lastResponse 
  } = useEmailService();

  // Initialize EmailJS on component mount
  useEffect(() => {
    initializeEmailJS();
  }, [initializeEmailJS]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      let response;
      
      switch (formType) {
        case 'franchise':
          response = await sendFranchiseInquiry({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            message: formData.message
          });
          break;
          
        case 'newsletter':
          response = await sendNewsletterSignup({
            email: formData.email,
            name: formData.name
          });
          break;
          
        default:
          response = await sendContactForm({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            message: formData.message,
            subject: formData.subject
          });
      }

      if (response.success) {
        // Clear form on success
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          subject: ''
        });
        
        onSuccess?.();
      }
    } catch (error) {
      // Handle form submission error silently in production
      if (process.env.NODE_ENV === 'development') {
        console.error('Form submission error:', error);
      }
    }
  };

  const getFormTitle = () => {
    if (title) return title;
    
    switch (formType) {
      case 'franchise':
        return '🚀 Join Royal Empire';
      case 'newsletter':
        return '📧 Stay Updated';
      default:
        return '📞 Get in Touch';
    }
  };

  const getFormSubtitle = () => {
    if (subtitle) return subtitle;
    
    switch (formType) {
      case 'franchise':
        return 'Ready to own your piece of the Royale with Cheese empire? Our franchise team will contact you within 24 hours.';
      case 'newsletter':
        return 'Get exclusive updates on new menu items, special offers, and events.';
      default:
        return 'Have a question or want to share feedback? We\'d love to hear from you!';
    }
  };

  const isFormValid = () => {
    if (formType === 'newsletter') {
      return formData.email.trim() !== '';
    }
    
    return formData.name.trim() !== '' && 
           formData.email.trim() !== '' && 
           (formType === 'franchise' ? formData.phone.trim() !== '' : true);
  };

  return (
    <FormContainer className={className}>
      <FormTitle>{getFormTitle()}</FormTitle>
      <FormSubtitle>{getFormSubtitle()}</FormSubtitle>
      
      <Form onSubmit={handleSubmit}>
        {formType !== 'newsletter' && (
          <FormGroup>
            <Label>
              <User size={16} />
              Full Name *
            </Label>
            <Input
              type="text"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
            />
          </FormGroup>
        )}

        <FormGroup>
          <Label>
            <Mail size={16} />
            Email Address *
          </Label>
          <Input
            type="email"
            placeholder="Enter your email address"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            required
          />
        </FormGroup>

        {(formType === 'contact' || formType === 'franchise') && (
          <FormGroup>
            <Label>
              <Phone size={16} />
              Phone Number {formType === 'franchise' ? '*' : '(Optional)'}
            </Label>
            <Input
              type="tel"
              placeholder="(313) 555-0123"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              required={formType === 'franchise'}
            />
          </FormGroup>
        )}

        {formType === 'contact' && (
          <FormGroup>
            <Label>
              <MessageSquare size={16} />
              Subject (Optional)
            </Label>
            <Input
              type="text"
              placeholder="What's this about?"
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
            />
          </FormGroup>
        )}

        {(formType === 'contact' || formType === 'franchise') && (
          <FormGroup>
            <Label>
              <MessageSquare size={16} />
              Message {formType === 'contact' ? '*' : '(Optional)'}
            </Label>
            <TextArea
              placeholder={
                formType === 'franchise' 
                  ? "Tell us about your interest in franchising, location preferences, and experience..."
                  : "Tell us what's on your mind..."
              }
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              required={formType === 'contact'}
            />
          </FormGroup>
        )}

        <SubmitButton 
          type="submit" 
          disabled={!isFormValid() || isLoading}
          isLoading={isLoading}
        >
          {isLoading ? (
            <>
              <Loader size={20} className="animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send size={20} />
              {formType === 'newsletter' ? 'Subscribe' : 'Send Message'}
            </>
          )}
        </SubmitButton>

        {lastResponse && (
          <StatusMessage type={lastResponse.success ? 'success' : 'error'}>
            {lastResponse.success ? (
              <CheckCircle size={20} />
            ) : (
              <AlertCircle size={20} />
            )}
            {lastResponse.message}
          </StatusMessage>
        )}

        {lastResponse && !lastResponse.success && (
          <FallbackButton onClick={fallbackToPhone}>
            <Phone size={16} />
            Call Us Instead: (313) 315-3014
          </FallbackButton>
        )}
      </Form>
    </FormContainer>
  );
};

export default ContactForm; 